import React, {useEffect, useRef, useState} from 'react'
import styles from './EntryForm.module.css'
import { useStepContext } from '@/components/Context/StepContext'
import { generateUrlSafeBase64 } from '@/components/utils/securityUtils'
import { handleFormSubmission } from '@/components/utils/formSubmissionUtils'
import { WorkingStatus } from '../Steps/StepContent/WorkingStatus'
import { ExperienceYears } from '../Steps/StepContent/ExperienceYears'
import { Prefecture } from '../Steps/StepContent/Prefecture'
import { FullName } from '../Steps/StepContent/FullName'
import { Tell } from '../Steps/StepContent/Tell'
import ProgressBar from '../Steps/ProgressBar'
import { EntryFormProps } from './types'
import { SERVICE_TYPES } from '@/components/constants'
import { JobChangeInterestModal } from '../FormGuidance/JobChangeInterestModal'
import { NextActionOverlay } from '../FormGuidance/NextActionOverlay'
import { FullPageOverlay } from '../FormGuidance/FullPageOverlay'
import { FormHeaderOverlay } from '../FormGuidance/FormHeaderOverlay'
import { useStableFlag } from '@/components/utils/hooks/useStableFlag'

const EntryForm = ({ prefectures, config }: EntryFormProps) => {
    const { currentStep, isAnimating, formData, isCurrentStepValid } = useStepContext()
    const [formBoxHeight, setFormBoxHeight] = useState('auto')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [registerToken, setRegisterToken] = useState('')
    const [step0ModalCompleted, setStep0ModalCompleted] = useState(false)
    const handleStep0ModalComplete = () => {
        setStep0ModalCompleted(true)
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }
    const prevStepRef = useRef(currentStep)
    const [showHighlight, setShowHighlight] = useState(false)

    useEffect(() => {
        const token = generateUrlSafeBase64()
        setRegisterToken(token)
    }, [])

    const isStep4Stable = useStableFlag(
        currentStep === 4 && isCurrentStepValid,
        500
    )

    useEffect(() => {
        const isMovingBackward = currentStep < prevStepRef.current
        prevStepRef.current = currentStep

        const isStepCompleted =
            (currentStep === 2 && !!formData.prefecture) ||
            (currentStep === 3 && isCurrentStepValid && formData.age.length >= 2) ||
            isStep4Stable
        setShowHighlight(isStepCompleted && !isMovingBackward)
    }, [isCurrentStepValid, currentStep, formData.prefecture, formData.age, isStep4Stable])

    useEffect(() => {
        if (currentStep === 0) {
            setFormBoxHeight('260px')
        } else if( currentStep === 4) {
            setFormBoxHeight('auto')
        } else {
            setFormBoxHeight('400px')
        }
    }, [currentStep])

    useEffect(() => {
        const handleGlobalTab = (e) => {
            if (e.key === 'Tab' || e.key === 'Enter') {
                e.preventDefault()
            }
        }
        document.addEventListener('keydown', handleGlobalTab)
        return () => {
            document.removeEventListener('keydown', handleGlobalTab)
        }
    }, [])

    const sliderStyle = {
        transform: `translateX(-${currentStep * 100}%)`
    }

    const handleSubmit = (e) => {
        handleFormSubmission(e, formData, registerToken, setIsSubmitting, SERVICE_TYPES.FOODSLABO)
    }
    return (
        <section id='entry-form'>
            <FullPageOverlay
                isVisible={showHighlight}
                onDismiss={() => setShowHighlight(false)}
            />

            {!step0ModalCompleted && (
                <JobChangeInterestModal
                    gtmTags={config.stepFormLabels.step0}
                    onComplete={handleStep0ModalComplete}
                />
            )}
            <div className={styles.entryFormSlideApp}>
                <section className={styles.container}>
                    <div
                        className={styles.formBox}
                        style={{
                            height: formBoxHeight,
                            transition: 'height 0.5s ease-in-out',
                            borderColor: showHighlight ? '#995700' : undefined
                        }}
                    >
                        <header className={styles.header}>
                            <FormHeaderOverlay
                                isVisible={showHighlight}
                                onDismiss={() => setShowHighlight(false)}
                            />
                            <h2 className={styles.title}>簡単30秒!</h2>
                            <h1 className={styles.subtitle}>無料で求人を紹介してもらう！</h1>
                        </header>

                        <div className={`${styles.stepProgressBar}`}>
                            <ProgressBar currentStep={currentStep}/>
                        </div>

                        <form
                            onSubmit={handleSubmit}
                        >
                            <div className={styles.formContainer}>
                                <div className={styles.formWrapper} style={sliderStyle}>
                                    <NextActionOverlay
                                        isVisible={showHighlight}
                                        onDismiss={() => setShowHighlight(false)}
                                    />
                                    <div className={styles.stepContent} >
                                        <WorkingStatus
                                            gtmTags={config?.stepFormLabels.step1}
                                        />
                                    </div>
                                    <div className={styles.stepContent}>
                                        <ExperienceYears gtmTags={config?.stepFormLabels.step2}/>
                                    </div>
                                    <div className={styles.stepContent} >
                                        <Prefecture
                                            prefectures={prefectures}
                                            gtmTags={config?.stepFormLabels.step3}
                                        />
                                    </div>
                                    <div className={styles.stepContent}>
                                        <FullName gtmTags={config?.stepFormLabels.step4}/>
                                    </div>
                                    <div
                                        className={`${styles.stepContent} ${currentStep === 4 ? styles.tellStep : ''}`}
                                    >
                                        <Tell gtmTags={config?.stepFormLabels.step5} isSubmitting={isSubmitting}/>
                                    </div>
                                </div>
                            </div>
                            {isAnimating && <div className={styles.blockingOverlay}/>}
                        </form>
                    </div>
                </section>
            </div>
        </section>
    )
}

export default EntryForm
